import { useState, useEffect, useCallback, useRef } from 'react';

interface UseScrollVisibilityOptions {
  /** Minimum scroll distance before triggering hide (default: 10) */
  threshold?: number;
  /** Initial visibility state (default: true) */
  initialVisible?: boolean;
}

/**
 * Hook to track scroll direction and determine if a fixed header should be visible.
 * - Scrolling down hides the header (returns false)
 * - Scrolling up shows the header (returns true)
 * - At the very top of the page, header is always visible
 */
export function useScrollVisibility(options: UseScrollVisibilityOptions = {}) {
  const { threshold = 10, initialVisible = true } = options;
  
  const [isVisible, setIsVisible] = useState(initialVisible);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    
    ticking.current = true;
    
    requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
      
      // Always show at the top of the page
      if (currentScrollY < threshold) {
        setIsVisible(true);
        lastScrollY.current = currentScrollY;
        ticking.current = false;
        return;
      }
      
      const scrollDelta = currentScrollY - lastScrollY.current;
      
      // Only trigger if scrolled past threshold
      if (Math.abs(scrollDelta) > threshold) {
        // Scrolling down - hide header
        if (scrollDelta > 0) {
          setIsVisible(false);
        }
        // Scrolling up - show header
        else {
          setIsVisible(true);
        }
        lastScrollY.current = currentScrollY;
      }
      
      ticking.current = false;
    });
  }, [threshold]);

  useEffect(() => {
    // Set initial scroll position
    lastScrollY.current = window.scrollY;
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return isVisible;
}
